import { useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import FilterListIcon from '@material-ui/icons/FilterList';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  popover: {
    padding: theme.spacing(2),
    minWidth: 260,
    maxWidth: 320,
  },
  section: {
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  chipGroup: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
    marginTop: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
}));

export interface MetricsFilters {
  showDevopsProjects: boolean;
  periodDays: number | null;
  showPrd: boolean;
  showHml: boolean;
  showDev: boolean;
}

export const DEFAULT_FILTERS: MetricsFilters = {
  showDevopsProjects: false,
  periodDays: null,
  showPrd: true,
  showHml: true,
  showDev: true,
};

interface MetricsFilterPopoverProps {
  filters: MetricsFilters;
  onChange: (filters: MetricsFilters) => void;
}

const PERIOD_OPTIONS = [
  { label: '1 dia', days: 1 },
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
];

export function MetricsFilterPopover({ filters, onChange }: MetricsFilterPopoverProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const update = (patch: Partial<MetricsFilters>) =>
    onChange({ ...filters, ...patch });

  // Count active filters (non-default)
  const activeCount = [
    filters.showDevopsProjects !== DEFAULT_FILTERS.showDevopsProjects,
    filters.periodDays !== DEFAULT_FILTERS.periodDays,
    !filters.showPrd,
    !filters.showHml,
    !filters.showDev,
  ].filter(Boolean).length;

  return (
    <>
      <IconButton
        onClick={e => setAnchorEl(e.currentTarget)}
        color={activeCount > 0 ? 'primary' : 'default'}
        data-testid="metrics-filter-button"
        title="Filtros"
        size="small"
      >
        <FilterListIcon />
        {activeCount > 0 && (
          <Typography
            variant="caption"
            color="primary"
            style={{ position: 'absolute', top: 2, right: 2, fontSize: 10, fontWeight: 700 }}
          >
            {activeCount}
          </Typography>
        )}
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box className={classes.popover}>
          {/* Period */}
          <div className={classes.section}>
            <Typography variant="body2" className={classes.sectionTitle}>
              Período
            </Typography>
            <div className={classes.chipGroup}>
              {PERIOD_OPTIONS.map(opt => (
                <Chip
                  key={opt.days}
                  label={opt.label}
                  size="small"
                  color={filters.periodDays === opt.days ? 'primary' : 'default'}
                  variant={filters.periodDays === opt.days ? 'default' : 'outlined'}
                  onClick={() =>
                    update({
                      periodDays: filters.periodDays === opt.days ? null : opt.days,
                    })
                  }
                  clickable
                />
              ))}
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* Environments */}
          <div className={classes.section}>
            <Typography variant="body2" className={classes.sectionTitle}>
              Ambientes
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showPrd}
                  onChange={e => update({ showPrd: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label="Exibir PRD"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showHml}
                  onChange={e => update({ showHml: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label="Exibir HML"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showDev}
                  onChange={e => update({ showDev: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label="Exibir DEV"
            />
          </div>

          <Divider className={classes.divider} />

          {/* Devops projects */}
          <div className={classes.section}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showDevopsProjects}
                  onChange={e => update({ showDevopsProjects: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label="Exibir projetos DevOps"
            />
          </div>
        </Box>
      </Popover>
    </>
  );
}
